import { useEffect, useCallback } from 'react';

/**
 * Lightweight proctoring for simulation sessions.
 * Reuses patterns from the existing AI Assessment proctoring
 * (src/Pages/InterviewRounds/hooks/useAiAssessmentViolationCapture.ts).
 *
 * Differences from the existing assessment proctoring:
 * - No webcam recording (not required for simulation in MVP)
 * - Fullscreen is encouraged but not enforced
 * - Tab switching logs a warning but does not disqualify
 * - Violations are tracked but the backend handles disconnection via heartbeats
 */
export function useProctoring(isActive: boolean) {
  // Disable right-click context menu
  const handleContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
  }, []);

  // Detect visibility changes (tab switches)
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      console.warn('[Proctoring] Tab switch detected');
    }
  }, []);

  // Detect suspicious key combinations
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const suspiciousKeys = ['F12', 'F5'];
    const suspiciousCombos =
      (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) ||
      (e.ctrlKey && ['u', 'U', 's', 'S'].includes(e.key)) ||
      (e.metaKey && e.altKey && ['I', 'J', 'C'].includes(e.key));

    if (suspiciousKeys.includes(e.key) || suspiciousCombos) {
      e.preventDefault();
      console.warn(`[Proctoring] Suspicious key detected: ${e.key}`);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, handleContextMenu, handleVisibilityChange, handleKeyDown]);
}
